import { useState, useEffect, useRef } from "react";
import { Helmet } from "react-helmet";
import { Route, Switch, useHistory, useParams } from "react-router-dom";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { cloneDeep } from "lodash";
import { ErrorBoundary } from "react-error-boundary";
import produce from "immer";
import { createUseStyles } from "react-jss";

import NodeEditor from "./NodeEditor";
import Floorplans from "./Floorplans";
import TourView from "./TourView";
import Autoplay from "./Autoplay";
import LoadingModal from "../components/LoadingModal";
import Toast from "../components/refresh/Toast";
import EmbedModal from "../components/EmbedModal";
import LinksModal from "../components/LinksModal";
import PublishModal from "../components/TourPublishModal";
import SettingsModal, {
  defaultOptions,
  defaultIframeOptions,
} from "../components/TourSettingsModal";
import MediaProvider from "../components/refresh/selectors/MediaSelector";
import ImageProvider from "../components/refresh/selectors/ImageSelector";
import GalleryProvider from "../components/refresh/selectors/GallerySelector";
import NewImageProvider from "../components/refresh/selectors/NewImageSelector";
import { fetchApi } from "../services/api";
import { createExampleTour } from "../utils";
import Hotspots from "./Hotspots";
import MapIntro from "./MapIntro";
import { linksContext as LinksContext } from "../components/ModeSelect";
import TextProvider from "../components/refresh/selectors/TextSelector";
import { currentAccount } from "../proxies";
import ErrorScreen from "./ErrorScreen";

const clearProxy = () => {
  currentAccount.account = null;
  currentAccount.accounts = [];
  currentAccount.activeAccount = null;
  currentAccount.team = null;
  currentAccount.teams = [];
  currentAccount.activeTeam = null;
  currentAccount.event = null;
  currentAccount.events = [];
};

const useStyles = createUseStyles({
  loading: {
    display: "flex",
    "align-items": "center",
    "justify-content": "center",
    height: "100vh",
    "background-color": "#252525",
    color: "white",
  },
});

const Editor = () => {
  const { token } = useParams();
  const history = useHistory();
  const [tour, setTour] = useState(null);
  const toastRef = useRef(null);
  const [activeImage, setActiveImage] = useState(null);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [loadedExample, setLoadedExample] = useState(false);
  const [currentInfo, setCurrentInfo] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showEmbed, setShowEmbed] = useState(false);
  const [showPublish, setShowPublish] = useState(false);
  const [showLinks, setShowLinks] = useState(false);
  const classes = useStyles();

  const onExit = () => {
    if (window.confirm("Are you sure you want to exit?")) {
      history.push("/");
    }
  };

  useEffect(() => {
    const onBeforeUnload = e => {
      if (loadedExample || dirty === false) return undefined;

      const message = "You haven't saved, are you sure you want to leave?";

      (e || window.event).returnValue = message;
      return message;
    };

    window.addEventListener("beforeunload", onBeforeUnload);

    const loadExample = window.localStorage.getItem("loadExample");
    if (loadExample !== null) {
      setTour(createExampleTour());
      setLoadedExample(true);
    }

    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [dirty, loadedExample]);

  const onUpdateActiveImage = image => {
    if (tour.data.images[image] !== undefined) {
      setActiveImage(image);
    } else {
      console.warn(`image ${image} not found, setting active to null`);
      setActiveImage(null);
    }
  };

  const onUpdateTour = newTour => {
    setTour(newTour);
    setDirty(true);
  };

  const onUpdateImage = (image, { setAsStart = false } = {}) => {
    setDirty(true);
    setTour(
      produce(tour, draft => {
        draft.data.images[image.id] = image;
        if (setAsStart) {
          draft.data.startImageId = image.id;
        }
      }),
    );

    onUpdateActiveImage(image.id);
  };

  const onUpdateBoomerang = (image_source, image_target) => {
    setDirty(true);
    setTour(
      produce(tour, draft => {
        draft.data.images[image_source.id] = image_source;
        draft.data.images[image_target.id] = image_target;
      }),
    );

    onUpdateActiveImage(image_source.id);
  };

  const onDeleteImage = image => {
    const images = cloneDeep(tour.data.images);
    delete images[image.id];

    // Warn if the deleted image is still referenced by Autoplay routes
    const flowsUsingImage = (tour.data.flows ?? []).filter(
      flow =>
        flow.start === image.id ||
        (flow.steps ?? []).some(step => step?.image === image.id),
    );
    if (flowsUsingImage.length > 0) {
      toastRef.current?.show(
        `The deleted image is used in ${flowsUsingImage.length} Autoplay route${
          flowsUsingImage.length !== 1 ? "s" : ""
        }: ${flowsUsingImage.map(flow => flow.name).join(", ")}`,
      );
    }

    // Search all hotspots to make sure none link to this one
    for (const id of Object.keys(images)) {
      if (images[id]?.hotspots !== undefined) {
        images[id].hotspots = images[id].hotspots.filter(item => {
          if (
            !["link", "elevate-up", "elevate-down", "fade"].includes(item.type)
          ) {
            return true;
          }

          if (item.options.id !== image.id) {
            return true;
          }

          return false;
        });
      }
    }

    setDirty(true);
    setActiveImage(null);
    setTour(
      produce(tour, draft => {
        draft.data.images = images;
      }),
    );
  };

  const onUpdateTourOptions = (options, iframeOptions) => {
    onUpdateTour(
      produce(tour, draft => {
        draft.data.options = { ...tour.data.options, ...options };
        draft.data.iframeOptions = {
          ...tour.data.iframeOptions,
          ...iframeOptions,
        };
      }),
    );
  };

  const onSaveTour = ({ overwrite = false, baseVersion } = {}) => {
    setSaving(true);

    const { team, id } = tour;

    const imgCount = Object.keys(tour.data.images).length;

    if (imgCount > 5000)
      return alert("Sorry, you can't save a tour with more than 5000 images");

    const savedTour = produce(tour, draft => {
      if (!draft.data.options) {
        draft.data.options = defaultOptions;
      }
      if (!draft.data.iframeOptions) {
        draft.data.iframeOptions = defaultIframeOptions;
      }

      draft.json = JSON.stringify(tour.data);
      delete draft.data;
      delete draft.team;
      delete draft.account;
      delete draft.token;
      delete draft.id;
      delete draft.thumbs;
      delete draft.thumbnail;

      if (baseVersion !== undefined) {
        draft.version = baseVersion;
      }
      draft.url = new Date().toString();
    });

    // Figure it where to send it!
    let url = `/tours/add/${team}`;
    let method = "POST";

    if (id !== undefined) {
      url = `/tours/edit/${id}`;
      method = "PATCH";
    }

    fetchApi(url, {
      body: JSON.stringify(savedTour),
      method,
      version: "v1",
      headers: {
        "Content-Type": "application/json",
      },
    })
      .then(response => {
        if (response.image_count) {
          if (imgCount !== response.image_count)
            alert(
              `Warning: after saving, there were a different number of images returned than were sent. Sent: ${imgCount}, Returned: ${response.image_count}`,
            );
        }
        // Merge state with response
        const updatedTour = Object.assign({}, tour, response);

        onUpdateTour(updatedTour);

        setSaving(false);
        setDirty(false);

        if (id === undefined) {
          history.replace(`/editor/${updatedTour.token}`);
        }
      })
      .catch(e => {
        console.error(e);
        setSaving(false);

        if (e?.status === 409) {
          if (overwrite) {
            const contentType = e.headers?.get("content-type") ?? "";
            if (contentType.includes("application/json")) {
              e.json()
                .then(errorData => {
                  window.alert(errorData?.message ?? "Overwrite failed.");
                })
                .catch(() => window.alert("Overwrite failed."));
            } else {
              window.alert(
                "Overwrite failed due to a conflict. Please reload and try again.",
              );
            }
            return;
          }

          const fetchLatestAndOverwrite = async () => {
            try {
              const latest = await fetchApi(`/tours/get/${tour.token}`);
              const latestVersion = latest?.version ?? latest?.data?.version;
              onSaveTour({
                overwrite: true,
                baseVersion: latestVersion,
              });
            } catch (err) {
              console.error(err);
              window.alert(
                "Couldn't fetch the latest version to overwrite. Please reload and try again.",
              );
            }
          };

          const confirmOverwrite = () =>
            window.confirm(
              "This tour has been updated elsewhere. Saving now will overwrite the newer version and may discard recent changes.\n\nDo you want to continue?",
            );

          if (confirmOverwrite()) fetchLatestAndOverwrite();
          return;
        }

        if (e.headers.get('content-type').includes('application/json')) {
          // Handle JSON error response
          e.json().then((errorData) => {
            if (errorData.messageType) {
              window.alert(errorData.message);
            } else {
              window.alert("An error occurred");
            }
          });
        } else {
          window.alert("An error occurred");
        }
      });
  };

  const onSaveCurrentInfo = value => {
    setCurrentInfo({
      x: value.x,
      y: value.y,
      scale: value.scale,
    });
  };

  const toggleEmbedPopup = () => {
    if (tour.token) {
      setShowEmbed(e => !e);
    } else {
      window.alert("You must save the tour before you can embed.");
    }
  };

  const onAddImage = image => {
    onUpdateImage(image);
  };

  const notDownloaded = tour === null;

  useEffect(() => {
    if (notDownloaded && token.match(/[A-Z0-9]{2}-[A-Z0-9]{4}/)) {
      clearProxy();

      fetchApi(`/tours/get/${token}`).then(response => {
        if (!response.data) {
          alert("Invalid response");
          history.replaceState("/");
        }
        if (
          response.data.images.length > 0 &&
          response.data.images[0].urls === undefined
        ) {
          alert("This tour is not supported in this version.");
          history.replaceState("/");
        } else {
          setTour(
            produce(response, draft => {
              if (
                !draft.data.options === undefined ||
                Array.isArray(draft.data.options)
              ) {
                draft.data.options = defaultOptions;
                draft.data.iframeOptions = defaultIframeOptions;
              }
            }),
          );
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notDownloaded, token]);

  if (notDownloaded) {
    if (!token.match(/(new|[A-Z0-9]{2}-[A-Z0-9]{4})/)) {
      const [account, team] = token.split("|");
      clearProxy();
      onUpdateTour({
        title: "New Tour",
        team: Number(team),
        account: Number(account),
        data: {
          images: {},
          options: defaultOptions,
          iframeOptions: defaultIframeOptions,
        },
      });
      history.replace("/editor/new");
    }
    return <div className={classes.loading}>Loading...</div>;
  }

  return (
    <DndProvider backend={HTML5Backend}>
      <MediaProvider account={tour.account}>
        <GalleryProvider account={tour.account} team={tour.team}>
          <NewImageProvider account={tour.account} team={tour.team}>
            <TextProvider>
              <ImageProvider
                images={tour?.data?.images ?? []}
                activeImage={activeImage}
              >
                <Helmet>
                  <title>{tour.title} | TouchTour360 Editor</title>
                </Helmet>
                <div>
                  {showSettings && (
                    <ErrorBoundary
                      fallback={<></>}
                      onError={() =>
                        alert(
                          "An error occured opening the settings menu. Please save your changes and try again",
                        )
                      }
                    >
                      <SettingsModal
                        onUpdateTourOptions={onUpdateTourOptions}
                        toggleTourSettings={() => setShowSettings(s => !s)}
                        togglePublishPopup={() => setShowPublish(s => !s)}
                        tourData={tour.data}
                        tour={tour}
                      />
                    </ErrorBoundary>
                  )}
                  {showEmbed && (
                    <EmbedModal
                      toggleEmbedPopup={toggleEmbedPopup}
                      url={`https://${window.location.host}/tour/${tour.token}`}
                    />
                  )}
                  {showPublish && (
                    <PublishModal
                      onUpdateTour={onUpdateTour}
                      togglePublishPopup={() => setShowPublish(s => !s)}
                      toggleTourSettings={() => setShowSettings(s => !s)}
                      tour={tour}
                    />
                  )}
                  <LinksModal
                    onUpdateTour={onUpdateTour}
                    show={showLinks}
                    setShowLinks={setShowLinks}
                    tour={tour}
                  />
                  <LoadingModal show={saving} message="Saving tour..." />
                  <Toast ref={toastRef} />
                  <LinksContext.Provider value={() => setShowLinks(true)}>
                    <ErrorBoundary fallback={<ErrorScreen />}>
                      <Switch>
                        <Route path="/editor/:token" exact>
                          <NodeEditor
                            tour={tour}
                            dirty={dirty}
                            onUpdateImage={onUpdateImage}
                            onUpdateBoomerang={onUpdateBoomerang}
                            onUpdateActiveImage={onUpdateActiveImage}
                            onAddImage={onAddImage}
                            onSaveTour={onSaveTour}
                            onExit={onExit}
                            onEmbedTour={toggleEmbedPopup}
                            onUpdateTour={onUpdateTour}
                            onDeleteImage={onDeleteImage}
                            onTourSettings={() => setShowSettings(true)}
                            activeImage={activeImage}
                            onSaveCurrentInfo={onSaveCurrentInfo}
                            currentInfo={currentInfo}
                          />
                        </Route>
                        <Route path="/editor/:token/360" exact>
                          <TourView
                            tour={tour}
                            onUpdateImage={onUpdateImage}
                            onUpdateBoomerang={onUpdateBoomerang}
                            onUpdateActiveImage={onUpdateActiveImage}
                            onUpdateTour={onUpdateTour}
                            onUpdateTourOptions={onUpdateTourOptions}
                            onTourSettings={() => setShowSettings(true)}
                            activeImage={activeImage}
                            onSaveTour={onSaveTour}
                            onEmbedTour={toggleEmbedPopup}
                          />
                        </Route>
                        <Route path="/editor/:token/floorplans" exact>
                          <Floorplans
                            tour={tour}
                            dirty={dirty}
                            onUpdateTour={onUpdateTour}
                            onEmbedTour={toggleEmbedPopup}
                            onSaveTour={onSaveTour}
                            onExit={onExit}
                            onTourSettings={() => setShowSettings(true)}
                          />
                        </Route>
                        <Route path="/editor/:token/hotspots" exact>
                          <Hotspots
                            tour={tour}
                            dirty={dirty}
                            onUpdateTour={onUpdateTour}
                            onEmbedTour={toggleEmbedPopup}
                            onSaveTour={onSaveTour}
                            onExit={onExit}
                            onTourSettings={() => setShowSettings(true)}
                          />
                        </Route>
                        <Route path="/editor/:token/mapIntro" exact>
                          <MapIntro
                            tour={tour}
                            dirty={dirty}
                            onUpdateTour={onUpdateTour}
                            onEmbedTour={toggleEmbedPopup}
                            onSaveTour={onSaveTour}
                            onExit={onExit}
                            onTourSettings={() => setShowSettings(true)}
                          />
                        </Route>
                        <Route path="/editor/:token/autoplay" exact>
                          <Autoplay
                            tour={tour}
                            dirty={dirty}
                            onUpdateTour={onUpdateTour}
                            onEmbedTour={toggleEmbedPopup}
                            onSaveTour={onSaveTour}
                            onExit={onExit}
                            onTourSettings={() => setShowSettings(true)}
                          />
                        </Route>
                      </Switch>
                    </ErrorBoundary>
                  </LinksContext.Provider>
                </div>
              </ImageProvider>
            </TextProvider>
          </NewImageProvider>
        </GalleryProvider>
      </MediaProvider>
    </DndProvider>
  );
};

export default Editor;
